import type { ComponentType } from "react";
import type { RouteAccess } from "@/router/types";

/**
 * US-009 — UI Surface Registry.
 *
 * A "surface" is a named mount point on a host page (a tab strip, a dashboard
 * card grid). A domain registers a `SurfaceItem` against a surface from its OWN
 * folder, so the host renders cross-domain UI without importing it — the same
 * decoupling `buildNav` gives the sidebar, one level down. Items are gated by
 * the SAME `RouteAccess` shape and `passesAccess` predicate routes and nav use
 * (see `useSurface`); the registry is advisory UX only, never an access boundary.
 *
 * Adding a `SurfaceId` is a deliberate decision that a surface is genuinely
 * cross-domain (or has churning gating) — NOT a default for "this page has tabs"
 * (US-009 adoption principle / Decision #6).
 */
export type SurfaceId =
  | "facility-detail-tabs"
  // US-010 — the dashboard is composed of layout regions, each its own surface so
  // a domain registers a card into the region it belongs to. Region render order
  // (set in DashboardPage) reproduces the pre-US-010 layout exactly:
  // banners → stats → highlights → summary → tables.
  | "dashboard-banners"
  | "dashboard-stats"
  | "dashboard-highlights"
  | "dashboard-summary"
  | "dashboard-tables";

export interface SurfaceItem {
  /** Stable, unique within the surface; also used as the Mantine tab `value`. */
  id: string;
  /** Tab/card title (omit for chrome-less cards). */
  label?: string;
  /** A Tabler icon component, rendered as `<icon size={16} />`. */
  icon?: ComponentType<{ size?: number | string }>;
  /** Ascending sort within the surface; mirrors `buildNav`'s `nav.order`. */
  order?: number;
  /**
   * Visibility gate — the SAME shape routes/nav use. Evaluated by `useSurface`
   * via the shared `passesAccess`. Omitted → visible to any portal member. The
   * item's own component keeps its own backend data gates.
   */
  access?: RouteAccess;
  /**
   * The (typically lazy, via `lazyNamed`) component to render. Props-less by
   * design — a wrapper reads any route params / context it needs, so the host
   * passes nothing and the registry stays generic.
   */
  Component: ComponentType;
}

/**
 * `order` ranges are reserved per contributing domain (AC-H5) so cross-domain
 * items don't collide into arbitrary tie-break churn as they're added:
 *   facility-detail-tabs: facility (local) 0–49, audit 50–99
 *   dashboard-banners:    residents 5 (clinical first), subscription 10, audit 20
 *   dashboard-stats:      residents 10, facilities 20, employees 30, incidents 40
 *   dashboard-highlights: residents 10, audit 20
 *   dashboard-summary:    residents 10, facilities 20
 *   dashboard-tables:     residents 10, facilities 20, audit 30
 */
const REGISTRY = new Map<SurfaceId, SurfaceItem[]>();

export function registerSurfaceItem(
  surface: SurfaceId,
  item: SurfaceItem,
): void {
  const items = REGISTRY.get(surface) ?? [];
  const existing = items.findIndex((i) => i.id === item.id);
  if (existing >= 0) {
    // HMR-safe (AC-H2): a Vite hot reload re-runs the side-effect registration
    // module, so a duplicate id is expected in dev — replace and warn, NEVER
    // throw (a throw here would crash the dev overlay on every save).
    if (import.meta.env.DEV) {
      console.warn(
        `[surfaces] duplicate item id "${item.id}" on surface "${surface}" — replacing existing registration.`,
      );
    }
    items[existing] = item;
  } else {
    items.push(item);
  }
  REGISTRY.set(surface, items);
}

/** Visible-order is the caller's job (`useSurface`); this returns ALL items for
 * the surface, sorted by `order`, as a fresh array (caller cannot mutate state). */
export function getSurfaceItems(surface: SurfaceId): SurfaceItem[] {
  return [...(REGISTRY.get(surface) ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
}

/** Test-only: reset global registry state between tests (AC-H4). */
export function __resetSurfaces(): void {
  REGISTRY.clear();
}
